/**
 * AgencyIntegrationsTab.jsx
 * ------------------------------------------------------------------
 * API keys for WP plugin / Shopify app / JS widget + download links
 * for plugin assets.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../../config";
import {
  Key, Plus, Trash2, Copy, Check, Loader2, Download, ExternalLink,
  AlertTriangle, ShoppingCart, Code,
} from "lucide-react";

export default function AgencyIntegrationsTab({ userEmail }) {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newKeyLabel, setNewKeyLabel] = useState("");
  const [newKeyScopes, setNewKeyScopes] = useState(["read", "scan", "geo"]);
  const [revealKey, setRevealKey] = useState(null);  // just-created plain text key
  const [copied, setCopied] = useState(false);
  const [shopify, setShopify] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/agency/api-keys`, {
        headers: { "X-User-Email": userEmail },
      });
      setKeys(r.data?.keys || []);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => {
    if (!userEmail) return;
    load();
    axios.get(`${API}/agency/integrations/shopify-app-info`, {
      headers: { "X-User-Email": userEmail },
    }).then(r => setShopify(r.data)).catch(() => {});
  }, [userEmail]);

  const handleCreate = async () => {
    if (!newKeyLabel.trim()) return;
    setCreating(true);
    try {
      const r = await axios.post(`${API}/agency/api-keys`,
        { label: newKeyLabel, scopes: newKeyScopes },
        { headers: { "X-User-Email": userEmail } });
      setRevealKey(r.data);
      setNewKeyLabel("");
      await load();
    } catch {} finally { setCreating(false); }
  };

  const handleRevoke = async (id) => {
    if (!confirm("Naozaj zrušiť tento API kľúč? Plugin/widget ho už nebude môcť používať.")) return;
    try {
      await axios.delete(`${API}/agency/api-keys/${id}`, {
        headers: { "X-User-Email": userEmail },
      });
      await load();
    } catch {}
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadWpPlugin = async () => {
    try {
      const r = await axios.get(`${API}/agency/integrations/wordpress-plugin.zip`, {
        headers: { "X-User-Email": userEmail },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url; a.download = "toprankpulse-wp-plugin.zip"; a.click();
      window.URL.revokeObjectURL(url);
    } catch {}
  };

  const downloadJsWidget = async () => {
    try {
      const r = await axios.get(`${API}/agency/integrations/js-widget.js`, {
        headers: { "X-User-Email": userEmail },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/javascript" }));
      const a = document.createElement("a");
      a.href = url; a.download = "trp-widget.js"; a.click();
      window.URL.revokeObjectURL(url);
    } catch {}
  };

  return (
    <div className="space-y-6" data-testid="agency-integrations-tab">
      <header>
        <h2 className="text-lg font-bold mb-1">Integrácie</h2>
        <p className="text-xs text-gray-400">Pripoj TopRankPulse k WordPress, Shopify alebo cez JS widget na ľubovoľnú stránku.</p>
      </header>

      {/* API keys */}
      <section className="p-5 bg-gray-900/40 border border-gray-800 rounded-sm">
        <h3 className="text-sm font-bold mb-3 flex items-center gap-2">
          <Key className="w-4 h-4 text-purple-400" /> API kľúče
        </h3>

        {revealKey && (
          <div className="p-4 mb-4 bg-emerald-900/20 border border-emerald-700/40 rounded-sm" data-testid="agency-key-reveal">
            <div className="flex items-start gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5" />
              <div className="text-xs text-amber-200">
                <b>Skopíruj si tento kľúč TERAZ</b> — po zatvorení už nebude dostupný.
              </div>
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-3 py-2 bg-black/60 border border-gray-800 text-emerald-300 font-mono text-xs break-all rounded-sm" data-testid="agency-key-reveal-value">
                {revealKey.key}
              </code>
              <button
                onClick={() => handleCopy(revealKey.key)}
                data-testid="agency-key-reveal-copy"
                className="px-3 py-2 bg-purple-600 hover:bg-purple-500 text-white text-sm rounded-sm inline-flex items-center gap-1"
              >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? "Skopírované" : "Kopírovať"}
              </button>
              <button
                onClick={() => setRevealKey(null)}
                className="px-2 py-2 bg-gray-800 hover:bg-gray-700 text-white text-sm rounded-sm"
              >
                Skryť
              </button>
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 mb-3">
          <input
            type="text"
            value={newKeyLabel}
            onChange={(e) => setNewKeyLabel(e.target.value)}
            placeholder="Popisok kľúča (napr. 'WP plugin')"
            data-testid="agency-key-label-input"
            className="flex-1 px-3 py-2 bg-black/50 border border-gray-800 focus:border-purple-500 text-white rounded-sm focus:outline-none text-sm"
          />
          <button
            onClick={handleCreate}
            disabled={creating || !newKeyLabel.trim()}
            data-testid="agency-key-create-btn"
            className="px-3 py-2 bg-purple-600 hover:bg-purple-500 disabled:opacity-40 text-white text-sm font-bold rounded-sm inline-flex items-center gap-1"
          >
            {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Vytvoriť kľúč
          </button>
        </div>

        {loading && <div className="text-gray-400"><Loader2 className="w-4 h-4 animate-spin inline" /></div>}
        {!loading && keys.length === 0 && !revealKey && (
          <div className="text-gray-500 text-xs">Žiadne aktívne API kľúče.</div>
        )}
        {keys.length > 0 && (
          <div className="overflow-x-auto border border-gray-900 rounded-sm">
            <table className="w-full text-xs">
              <thead className="bg-black/50 text-gray-500 uppercase tracking-widest">
                <tr>
                  <th className="px-3 py-2 text-left">Label</th>
                  <th className="px-3 py-2 text-left">Kľúč preview</th>
                  <th className="px-3 py-2 text-left">Scopes</th>
                  <th className="px-3 py-2 text-left">Posledné použitie</th>
                  <th className="px-3 py-2 text-right">Akcie</th>
                </tr>
              </thead>
              <tbody>
                {keys.map((k, i) => (
                  <tr key={k.id} className="border-t border-gray-900" data-testid={`agency-key-row-${i}`}>
                    <td className="px-3 py-2 text-white font-bold">{k.label}</td>
                    <td className="px-3 py-2 text-gray-400 font-mono">{k.key_preview}</td>
                    <td className="px-3 py-2 text-purple-300">{(k.scopes || []).join(", ") || "—"}</td>
                    <td className="px-3 py-2 text-gray-500">{k.last_used_at ? new Date(k.last_used_at).toLocaleString("sk-SK") : "—"}</td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => handleRevoke(k.id)}
                        data-testid={`agency-key-revoke-${i}`}
                        className="px-2 py-1 bg-rose-900/40 hover:bg-rose-900 text-rose-200 text-xs rounded-sm inline-flex items-center gap-1"
                      >
                        <Trash2 className="w-3 h-3" /> Zrušiť
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* WP plugin */}
      <section className="p-5 bg-gray-900/40 border border-gray-800 rounded-sm">
        <h3 className="text-sm font-bold mb-2 flex items-center gap-2">
          <Code className="w-4 h-4 text-purple-400" /> WordPress Plugin
        </h3>
        <p className="text-xs text-gray-400 mb-3">
          Stiahni .zip a nainštaluj v WP admin → Plugins → Add New → Upload. Po aktivácii
          vlož API key v Settings → TopRankPulse.
        </p>
        <button
          onClick={downloadWpPlugin}
          data-testid="agency-wp-plugin-download"
          className="px-3 py-2 bg-purple-600 hover:bg-purple-500 text-white text-sm font-bold rounded-sm inline-flex items-center gap-1"
        >
          <Download className="w-3.5 h-3.5" /> Stiahnuť WP plugin .zip
        </button>
      </section>

      {/* JS Widget */}
      <section className="p-5 bg-gray-900/40 border border-gray-800 rounded-sm">
        <h3 className="text-sm font-bold mb-2 flex items-center gap-2">
          <Code className="w-4 h-4 text-purple-400" /> JS Widget
        </h3>
        <p className="text-xs text-gray-400 mb-3">
          Vlož na ľubovoľnú stránku — pridá "Audited by TopRankPulse" badge:
        </p>
        <pre className="px-3 py-2 mb-3 bg-black/60 border border-gray-800 text-emerald-300 font-mono text-xs overflow-x-auto whitespace-pre-wrap rounded-sm" data-testid="agency-js-widget-snippet">
{`<script src="${API}/agency/integrations/js-widget.js"
        data-trp-key="trp_..."
        data-trp-domain="myshop.sk"
        async></script>`}
        </pre>
        <button
          onClick={downloadJsWidget}
          data-testid="agency-js-widget-download"
          className="px-3 py-2 bg-gray-800 hover:bg-gray-700 text-white text-sm rounded-sm inline-flex items-center gap-1"
        >
          <Download className="w-3.5 h-3.5" /> Stiahnuť trp-widget.js
        </button>
      </section>

      {/* Shopify App */}
      <section className="p-5 bg-gray-900/40 border border-gray-800 rounded-sm">
        <h3 className="text-sm font-bold mb-2 flex items-center gap-2">
          <ShoppingCart className="w-4 h-4 text-purple-400" /> Shopify App
        </h3>
        {shopify ? (
          <div className="space-y-2 text-xs text-gray-300">
            <div>Status: <span className={shopify.status === "pending_partner_approval" ? "text-amber-300" : "text-emerald-300"}>{shopify.status}</span></div>
            <div>Install URL: <code className="text-purple-300">{shopify.install_url}</code></div>
            <a
              href={shopify.documentation} target="_blank" rel="noreferrer"
              data-testid="agency-shopify-docs"
              className="inline-flex items-center gap-1 text-purple-400 hover:underline"
            >
              <ExternalLink className="w-3 h-3" /> Dokumentácia
            </a>
          </div>
        ) : (
          <div className="text-gray-500 text-xs">Načítam Shopify info…</div>
        )}
      </section>
    </div>
  );
}
